import { Button, HFlow, Icon, TableFooter, Text, Tooltip, useTheme } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { useAlert } from 'components/alert'
import { confirm } from 'components/modals/confirm'
import { TableBox } from 'components/table'
import { usePagination } from 'components/table/usePagination'
import { CategoriaArquivoAtendProfEnum, Cbo } from 'graphql/types.generated'
import { useFilter } from 'hooks/filter/useFilter'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { emptyArrayOf } from 'util/array'
import { formatDate } from 'util/date/formatDate'
import { noop } from 'util/functions'
import { ProfissionalAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'

import {
  AnexoArquivoFilterModel,
  AnexoArquivoFormModel,
  AnexoArquivoRowModel,
  categoriaAnexoLabelRecord,
} from '../../model-anexoArquivos'
import { mergeSortAnexoArquivos } from '../../utils/mergeSortAnexoArquivos'
import { sortAnexoArquivos, SortAnexoArquivosField } from '../../utils/sortAnexoArquivos'
import { VisualizarArquivoAnexadoTableButton } from '../VisualizarArquivoAnexadoTableButton'
import { AnexoArquivosAccordionPanel } from './AnexoArquivosAccordionPanel'
import { AnexoArquivosTableHeader } from './AnexoArquivosTableHeader'
import { AnexoArquivosTableRow } from './AnexoArquivosTableRow'

interface AnexoArquivosTableBaseProps {
  arquivosAnexadosDatabase?: ReadonlyArray<AnexoArquivoRowModel>
  profissional?: ProfissionalAtendimento
}

interface AnexoArquivosTableReadOnlyProps extends AnexoArquivosTableBaseProps {
  readOnly: true
  anexoArquivosLoading?: boolean
}

interface AnexoArquivosTableEditavelProps extends AnexoArquivosTableBaseProps {
  readOnly?: false
  atendimentoProfissionalId: ID
  profissional: ProfissionalAtendimento
  lotacaoId: ID
  cbo: Cbo
  arquivosAnexados?: AnexoArquivoFormModel[]
  anexoArquivosLoading: boolean
  onRemoveRow?(cacheId: ID): void
  onUpdateRow?(cacheId: ID, value: AnexoArquivoFormModel): void
}

type AnexoArquivosTableProps = AnexoArquivosTableReadOnlyProps | AnexoArquivosTableEditavelProps

function isModoEdicao(props: AnexoArquivosTableProps): props is AnexoArquivosTableEditavelProps {
  return !props.readOnly
}

const handleFilterByCategoria = (categoria: CategoriaArquivoAtendProfEnum) => (anexoArquivo: AnexoArquivoRowModel) =>
  categoria ? anexoArquivo.categoria === categoria : true

const handleFilterBySomenteMeus = (shouldFilter: boolean, profissionalId: ID) => (
  anexoArquivo: AnexoArquivoRowModel
) => {
  return shouldFilter ? anexoArquivo.atendimentoProfissional.lotacao.profissional.id === profissionalId : true
}

export function AnexoArquivosTable(props: AnexoArquivosTableProps) {
  const arquivosAnexadosDatabase = props.arquivosAnexadosDatabase ?? emptyArrayOf<AnexoArquivoRowModel>()
  const readOnly = !!props.readOnly

  const { ...accordionProps } = useAccordionControl({})

  const [sort, setSort] = useState<SortAnexoArquivosField[]>(['-data'])
  const [editingRowId, setEditingRowId] = useState<ID | null>(null)

  const isEditing = useCallback((cacheId?: ID): boolean => cacheId !== undefined && cacheId === editingRowId, [
    editingRowId,
  ])

  const propsEdicao = isModoEdicao(props) ? props : undefined
  const arquivosAnexadosForm = propsEdicao?.arquivosAnexados
  const atendimentoProfissionalId = propsEdicao?.atendimentoProfissionalId
  const lotacaoId = propsEdicao?.lotacaoId
  const profissional = propsEdicao?.profissional
  const cbo = propsEdicao?.cbo

  const onUpdateRow = propsEdicao?.onUpdateRow ?? noop
  const onRemoveRow = propsEdicao?.onRemoveRow ?? noop
  const anexoArquivosLoading = props.anexoArquivosLoading ?? false

  const arquivosAnexadosMerged = useMemo(() => {
    if (readOnly || !atendimentoProfissionalId || !lotacaoId || !profissional || !cbo) {
      return sortAnexoArquivos(arquivosAnexadosDatabase, sort)
    }

    return mergeSortAnexoArquivos(
      arquivosAnexadosDatabase,
      arquivosAnexadosForm ?? emptyArrayOf<AnexoArquivoFormModel>(),
      atendimentoProfissionalId,
      lotacaoId,
      profissional,
      cbo,
      sort
    )
  }, [
    readOnly,
    arquivosAnexadosDatabase,
    sort,
    arquivosAnexadosForm,
    atendimentoProfissionalId,
    lotacaoId,
    profissional,
    cbo,
  ])

  const [filter, setFilter] = useState<AnexoArquivoFilterModel>({ categoria: null, somenteMeus: false })

  const anexoArquivosFiltrados = useFilter<AnexoArquivoRowModel, AnexoArquivoFilterModel>({
    items: arquivosAnexadosMerged,
    filter: filter,
    filtersType: [],
    customFilters: [
      handleFilterByCategoria(filter.categoria),
      handleFilterBySomenteMeus(filter.somenteMeus, props.profissional?.id),
    ],
  })

  const { paginatedItems, tableProps } = usePagination<AnexoArquivoRowModel>({
    items: anexoArquivosFiltrados,
  })

  const theme = useTheme()
  const alert = useAlert()

  const component = useMemo(
    () => ({
      AccordionPanel: AnexoArquivosAccordionPanel,
      Row: (props) => (
        <AnexoArquivosTableRow
          isEditing={isEditing(props.row.cacheId)}
          onUpdate={onUpdateRow}
          onCancel={() => setEditingRowId(null)}
          {...props}
        />
      ),
    }),
    [isEditing, onUpdateRow]
  )

  const handleEdit = (e: MouseEvent, cacheId?: ID) => {
    e.stopPropagation()
    if (cacheId !== undefined) setEditingRowId(cacheId)
  }

  const handleRemove = (e: MouseEvent, cacheId?: ID) => {
    e.stopPropagation()
    if (cacheId === undefined) return
    confirm({
      title: 'Deseja excluir o arquivo anexado?',
      onConfirm: () => {
        onRemoveRow(cacheId)
        alert('success', 'Anexo de arquivo excluído com sucesso')
      },
      confirmLabel: 'Excluir',
    })()
  }

  return (
    <TableBox header={<AnexoArquivosTableHeader onFilterChange={setFilter} currentFilter={filter} />}>
      <AccordionDataTable<AnexoArquivoRowModel>
        {...accordionProps}
        loading={anexoArquivosLoading}
        rows={paginatedItems}
        columns={[
          {
            name: 'data',
            header: 'Data',
            render: (item: AnexoArquivoRowModel) => {
              const isItemFromDatabase = !!item.id
              return (
                <HFlow alignItems='center'>
                  <Text>{formatDate(item.arquivo.dataCriacao)}</Text>
                  {!isItemFromDatabase && (
                    <Tooltip text='Adicionado agora'>
                      <Icon icon='clockOutline' color={theme.pallete.primary.c40} size={1} />
                    </Tooltip>
                  )}
                </HFlow>
              )
            },
            size: 2,
            sortable: true,
          },
          {
            name: 'categoria',
            header: 'Categoria do arquivo',
            render: (item: AnexoArquivoRowModel) => categoriaAnexoLabelRecord[item.categoria],
            size: 3,
            sortable: false,
          },
          {
            name: 'titulo',
            header: 'Título',
            render: (item: AnexoArquivoRowModel) => item.titulo,
            size: 5,
            sortable: false,
          },
          {
            name: 'buttons',
            header: '',
            render: (item: AnexoArquivoRowModel) => {
              const isItemFromDatabase = !!item.id

              return (
                <HFlow hSpacing={0.5} alignItems='center' justifyContent='flex-end'>
                  <HFlow hSpacing={0} alignItems='center' justifyContent='flex-end'>
                    <VisualizarArquivoAnexadoTableButton arquivoAnexado={item} />
                    {!isItemFromDatabase && !readOnly && (
                      <>
                        <Tooltip text='Editar'>
                          <Button
                            size='small'
                            kind='normal'
                            skin='ghost'
                            onClick={(e: MouseEvent) => handleEdit(e, item.cacheId)}
                            data-testid='EditarArquivoAnexadoButton'
                          >
                            <Icon icon='penOutline' />
                          </Button>
                        </Tooltip>
                        <Tooltip text='Excluir'>
                          <Button
                            size='small'
                            kind='normal'
                            skin='ghost'
                            onClick={(e: MouseEvent) => handleRemove(e, item.cacheId)}
                            data-testid='ExcluirArquivoAnexadoButton'
                          >
                            <Icon icon='trashOutline' />
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </HFlow>
                </HFlow>
              )
            },
            size: 2,
            sortable: false,
          },
        ]}
        components={component}
        sort={sort}
        onSortChange={(sort: string[]) => setSort(sort as SortAnexoArquivosField[])}
        disableRow={(row) => isEditing(row.cacheId)}
      />
      <TableFooter {...tableProps} />
    </TableBox>
  )
}
